import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { selectLoginResponse } from '../../redux/login/login.selector';
import Icon from '../../components/icon/Icon';
import { getCompOffReportStart } from '../../redux/NewReport/NewReport.actions';
import { selectCompOffReportResponse } from '../../redux/NewReport/NewReport.selectors';
import Input from '../../components/bootstrap/forms/Input';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Label from '../../components/bootstrap/forms/Label';
import showNotification from '../../components/extras/showNotification';

interface CompOffRecord {
	Employee_ID: string;
	Employee_Name: string;
	Designation: string;
	branchName: string;
	Work_Date: string;
	Valid_Till: string;
	Reason: string;
	Status: string;
	Approved_By: string | null;
	Rejected_Reason: string | null;
	Created_On: string;
	Status_Label: string;
	Is_Used: number;
	Used_On: string | null;
	compOffID: number;
}

const CompOffReportPage = () => {
	const dispatch = useDispatch();
	const loginData = useSelector(selectLoginResponse);
    const compOffResp = useSelector(selectCompOffReportResponse);
    const compOffReportData = compOffResp ?? null;

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');

	// Set default dates (current month)
	useEffect(() => {
		const today = new Date();
		const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
		const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
		
		setStartDate(firstDay.toISOString().split('T')[0]);
		setEndDate(lastDay.toISOString().split('T')[0]);
	}, []);

	// Handle fetch report
	const handleFetchReport = () => {
		if (!startDate || !endDate) {
			showNotification(
				'Warning',
				'Please select both start and end dates',
				'warning'
			);
			return;
		}

    dispatch(getCompOffReportStart({
            organisationID: loginData?.data?.organisationID || 1,
            startDate: startDate,
            endDate: endDate,
    }));
	};

	// Get status badge color
	const getStatusBadgeColor = (status: string, isUsed: number) => {
		if (isUsed === 1) return 'success';
		switch (status) {
			case 'Yet To Be Approved':
				return 'warning';
			case 'Approved':
				return 'info';
			case 'Rejected':
				return 'danger';
			default:
				return 'secondary';
		}
	};

	// Export to Excel
	const handleExportToExcel = () => {
		const data = compOffReportData?.data || [];
		if (data.length === 0) {
			showNotification('Warning', 'No data to export', 'warning');
			return;
		}

		// Create CSV content
		const headers = [
			'Employee ID',
			'Employee Name',
			'Designation',
			'Branch',
			'Work Date',
			'Valid Till',
			'Reason',
			'Status',
			'Approved By',
			'Is Used',
			'Used On',
			'Created On',
		];
		
		const csvRows = [headers.join(',')];
		
		data.forEach((record: CompOffRecord) => {
			const row = [
				record.Employee_ID,
				`"${record.Employee_Name}"`,
				`"${record.Designation}"`,
				`"${record.branchName}"`,
				record.Work_Date,
				record.Valid_Till || '',
				`"${record.Reason || ''}"`,
				record.Status_Label,
				record.Approved_By || '',
				record.Is_Used === 1 ? 'Yes' : 'No',
				formatDateOnly(record.Used_On || ''),
				formatDateOnly(record.Created_On),
			];
			csvRows.push(row.join(','));
		});

		// Download
		const csvString = csvRows.join('\n');
		const blob = new Blob([csvString], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `CompOff_Report_${startDate}_to_${endDate}.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	const formatDateOnly = (value: string | null): string => {
		if (!value) return '-';
		// Try splitting on space or 'T'
		const parts = value.includes(' ') ? value.split(' ') : value.split('T');
		if (parts[0] && /^\d{4}-\d{2}-\d{2}$/.test(parts[0])) return parts[0];
		// Fallback: return as-is
		return value;
	};

	return (
		<PageWrapper title='CompOff Report'>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='Description' className='me-2' size='2x' />
					<span className='fs-4'>Compensatory Off (CompOff) Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<Card stretch>
					<CardHeader>
						<CardLabel icon='CalendarToday' iconColor='primary'>
							<CardTitle>CompOff Report</CardTitle>
						</CardLabel>
					</CardHeader>
					<CardBody>
						<div className='row g-3 mb-4'>
							<div className='col-md-3'>
								<FormGroup>
									<Label htmlFor='startDate'>Start Date</Label>
									<Input
										type='date'
										id='startDate'
										value={startDate}
										onChange={(e: any) => setStartDate(e.target.value)}
									/>
								</FormGroup>
							</div>
							<div className='col-md-3'>
								<FormGroup>
									<Label htmlFor='endDate'>End Date</Label>
									<Input
										type='date'
										id='endDate'
										value={endDate}
										onChange={(e: any) => setEndDate(e.target.value)}
									/>
								</FormGroup>
							</div>
							<div className='col-md-3 d-flex align-items-end'>
								<Button
									color='primary'
									icon='Search'
									onClick={handleFetchReport}>
									Fetch Report
								</Button>
							</div>
							<div className='col-md-3 d-flex align-items-end justify-content-end'>
								<Button
									color='success'
									icon='FileDownload'
									onClick={handleExportToExcel}
									isDisable={!compOffReportData?.data || compOffReportData.data.length === 0}>
									Export to Excel
								</Button>
							</div>
						</div>

						{compOffReportData && compOffReportData.status === 'success' && (
							<div className='table-responsive'>
								<table className='table table-modern table-hover'>
									<thead>
										<tr>
											<th>S.No</th>
											<th>Emp ID</th>
											<th>Employee Name</th>
											<th>Designation</th>
											<th>Branch</th>
											<th>Work Date</th>
											<th>Valid Till</th>
											<th>Reason</th>
											<th>Status</th>
											<th>Used</th>
									<th>Used On</th>
											<th>Created On</th>
										</tr>
									</thead>
									<tbody>
										{compOffReportData.data && compOffReportData.data.length > 0 ? (
											compOffReportData.data.map((record: CompOffRecord, index: number) => (
												<tr key={record.compOffID}>
													<td>{index + 1}</td>
													<td>{record.Employee_ID}</td>
													<td>{record.Employee_Name}</td>
													<td>{record.Designation}</td>
													<td>{record.branchName}</td>
													<td>{record.Work_Date}</td>
													<td>{record.Valid_Till || '-'}</td>
													<td className='text-truncate' style={{ maxWidth: '200px' }} title={record.Reason}>
														{record.Reason || '-'}
													</td>
													<td>
														<span className={`badge bg-${getStatusBadgeColor(record.Status, record.Is_Used)}`}>
															{record.Status_Label}
														</span>
													</td>
													<td>
														{record.Is_Used === 1 ? (
															<span className='badge bg-success'>
																<Icon icon='CheckCircle' /> Yes
															</span>
														) : (
															<span className='badge bg-secondary'>No</span>
														)}
													</td>
											<td>{formatDateOnly(record.Used_On)}</td>
											<td>{formatDateOnly(record.Created_On)}</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan={11} className='text-center'>
													No comp off records found for the selected date range
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						)}

						{compOffReportData && compOffReportData.status === 'error' && (
							<div className='alert alert-danger'>
								<Icon icon='Warning' className='me-2' />
								{compOffReportData.message || 'Failed to fetch comp off report'}
							</div>
						)}
					</CardBody>
				</Card>
			</Page>
		</PageWrapper>
	);
};

export default CompOffReportPage;
